import { Link, router, useForm, usePage } from "@inertiajs/react"
import { Check, Copy, Wifi } from "lucide-react"
import { type ReactNode, useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  OnboardingStatus,
  PropertyDetail,
  PropertyStatus,
  PropertyType,
  RegionOption,
  RegionReadiness,
  WifiConfig,
} from "@/lib/types"
import type { SharedProps } from "@/types"

interface Props {
  property: PropertyDetail
  region_readiness: RegionReadiness | null
  all_regions: RegionOption[]
  can_suspend: boolean
  can_reactivate: boolean
  can_mark_live: boolean
  urls: { index: string; update: string; update_wifi: string; mark_live: string; suspend: string; reactivate: string }
}

function statusVariant(status: PropertyStatus): "default" | "outline" | "destructive" {
  if (status === "active") return "default"
  if (status === "suspended") return "destructive"
  return "outline"
}

function onboardingClassName(status: OnboardingStatus): string {
  const map: Record<OnboardingStatus, string> = {
    pending:         "border-yellow-300 bg-yellow-50 text-yellow-700",
    materials_sent:  "border-blue-300 bg-blue-50 text-blue-700",
    wifi_configured: "border-purple-300 bg-purple-50 text-purple-700",
    live:            "border-green-300 bg-green-50 text-green-700",
  }
  return map[status] ?? ""
}

function onboardingLabel(status: OnboardingStatus): string {
  const map: Record<OnboardingStatus, string> = {
    pending:         "Pending",
    materials_sent:  "Materials sent",
    wifi_configured: "WiFi configured",
    live:            "Live",
  }
  return map[status] ?? status
}

function onboardingProgress(status: OnboardingStatus): number {
  const map: Record<OnboardingStatus, number> = {
    pending:         25,
    materials_sent:  50,
    wifi_configured: 75,
    live:            100,
  }
  return map[status] ?? 0
}

const WIFI_INTEGRATION_TYPES = [
  { value: "post_login_redirect", label: "Post-login redirect" },
  { value: "captive_portal",      label: "Captive portal" },
  { value: "qr_only",             label: "QR only (no WiFi redirect)" },
] as const

const WIFI_INSTRUCTIONS: Record<string, { title: string; steps: string[] }> = {
  post_login_redirect: {
    title: "How to set up post-login redirect",
    steps: [
      "Log in to your WiFi admin panel",
      "Find the redirect URL or post-login URL setting",
      "Paste the redirect URL shown below",
      "Save your changes",
    ],
  },
  captive_portal: {
    title: "How to set up captive portal",
    steps: [
      "Contact your WiFi provider or network administrator",
      "Request them to embed the INNLY guide on your captive portal login page",
      "Provide them the redirect URL shown below",
    ],
  },
  qr_only: {
    title: "How to set up QR distribution",
    steps: [
      "Go to the Delivery page to download your QR code",
      "Print the QR code and place it at reception or in guest rooms",
      "No WiFi configuration required",
    ],
  },
}

function GoLivePanel({ markLiveUrl }: { markLiveUrl: string }) {
  const [launching, setLaunching] = useState(false)

  function handleGoLive() {
    setLaunching(true)
    router.patch(markLiveUrl, {}, { onFinish: () => setLaunching(false) })
  }

  return (
    <Card className="border-green-300 bg-green-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-green-800">Ready to go live</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        <p className="text-sm text-green-700">
          Your region meets the launch requirements. Publishing makes your guide live for guests
          via QR codes and the WiFi redirect.
        </p>
        <div>
          <Button
            size="sm"
            className="bg-green-700 text-white hover:bg-green-800"
            disabled={launching}
            onClick={handleGoLive}
          >
            {launching ? "Going live…" : "Go live"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function RegionReadinessAlert({
  readiness,
  markLiveUrl,
  canForce,
}: {
  readiness: RegionReadiness
  markLiveUrl: string
  canForce: boolean
}) {
  const [forcing, setForcing] = useState(false)

  if (readiness.ready) return null

  function handleForceLaunch() {
    setForcing(true)
    router.patch(markLiveUrl, { force: true }, { onFinish: () => setForcing(false) })
  }

  const conditions = [
    {
      label: "At least 1 distributor assigned",
      met: readiness.has_distributor,
    },
    {
      label: `At least ${readiness.min_launch_pois} approved public POIs (currently ${readiness.poi_count})`,
      met: readiness.poi_count >= readiness.min_launch_pois,
    },
    {
      label: `At least 1 POI verified within the last ${readiness.max_stale_days} days`,
      met: readiness.has_fresh_content,
    },
  ]

  return (
    <Card role="alert" className="border-amber-300 bg-amber-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-amber-800">
          Region not ready for launch
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        <ul className="grid gap-1 text-sm">
          {conditions.map((c) => (
            <li key={c.label} className="flex items-start gap-2">
              <span
                className={c.met ? "text-green-600 font-bold" : "text-red-500 font-bold"}
                aria-hidden="true"
              >
                {c.met ? "✓" : "✗"}
              </span>
              <span className={c.met ? "text-amber-700" : "text-amber-900 font-medium"}>
                {c.label}
              </span>
            </li>
          ))}
        </ul>

        {canForce ? (
          <div className="mt-2 flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="border-amber-400 text-amber-800 hover:bg-amber-100">
                  Force Launch
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Force launch despite unmet conditions?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The region does not meet all readiness conditions. Forcing launch will mark this
                    property as live and log a warning for audit purposes. This action should only be
                    used by platform admins who have verified the situation manually.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleForceLaunch} disabled={forcing}>
                    {forcing ? "Launching…" : "Yes, force launch"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <p className="text-xs text-amber-700">Platform admin only — logged for audit.</p>
          </div>
        ) : (
          <p className="mt-2 text-xs text-amber-700">
            These items are curated by your region’s local guide. Contact your Innly admin if you
            need to launch before they’re met.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export function WifiSetupForm({ property, updateWifiUrl }: { property: PropertyDetail; updateWifiUrl: string }) {
  const wifiConfig = property.wifi_config
  const redirectUrl = property.redirect_url
  const [copied, setCopied] = useState(false)
  const [marking, setMarking] = useState(false)

  const { data, setData, patch, processing, errors } = useForm({
    wifi: {
      integration_type: (wifiConfig?.integration_type ?? "qr_only") as WifiConfig["integration_type"],
      mark_configured:  false as boolean,
    },
  })

  const instructions = WIFI_INSTRUCTIONS[data.wifi.integration_type]
  const busy = processing || marking

  function handleCopy() {
    navigator.clipboard.writeText(redirectUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    patch(updateWifiUrl)
  }

  function handleMarkConfigured() {
    setMarking(true)
    router.patch(
      updateWifiUrl,
      { wifi: { integration_type: data.wifi.integration_type, mark_configured: true } },
      { onFinish: () => setMarking(false) },
    )
  }

  return (
    <div className="grid gap-5">
      {wifiConfig?.is_configured ? (
        <div className="flex items-center gap-2">
          <Badge variant="default" className="text-xs">Configured</Badge>
          {wifiConfig.configured_at && (
            <span className="text-xs text-muted-foreground">
              on {new Date(wifiConfig.configured_at).toLocaleDateString()}
            </span>
          )}
          <span className="text-xs text-muted-foreground">· You can still change the integration type below.</span>
        </div>
      ) : null}

      <form onSubmit={handleSave} className="grid max-w-lg gap-5">
        <div className="grid gap-1.5">
          <Label htmlFor="integration_type">Integration type</Label>
          <Select
            value={data.wifi.integration_type}
            onValueChange={(v) =>
              setData("wifi", { ...data.wifi, integration_type: v as WifiConfig["integration_type"] })
            }
          >
            <SelectTrigger id="integration_type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WIFI_INTEGRATION_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors["wifi.integration_type"] && (
            <p className="text-xs text-destructive">{errors["wifi.integration_type"]}</p>
          )}
        </div>

        {data.wifi.integration_type !== "qr_only" && (
          <div className="grid gap-1.5">
            <Label>Redirect URL</Label>
            <div className="flex items-center gap-2">
              <Input value={redirectUrl} readOnly className="bg-muted font-mono text-xs" />
              <Button
                type="button"
                variant="outline"
                className="shrink-0 gap-1.5"
                onClick={handleCopy}
                aria-label="Copy redirect URL"
              >
                {copied ? (
                  <>
                    <Check className="size-4 text-green-600" />
                    <span className="text-xs">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="size-4" />
                    <span className="text-xs">Copy</span>
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              This URL is auto-generated from the property slug. Copy it into your WiFi admin panel.
            </p>
          </div>
        )}

        {instructions && (
          <div className="rounded-lg border bg-muted/40 p-4">
            <p className="mb-2 text-sm font-medium">{instructions.title}</p>
            <ol className="grid list-decimal gap-1 pl-4 text-sm text-muted-foreground">
              {instructions.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={busy}>
            {processing ? "Saving…" : "Save integration type"}
          </Button>
          {!wifiConfig?.is_configured && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                >
                  <Wifi className="mr-2 size-4" />
                  Mark as configured
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Mark WiFi as configured?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This confirms that WiFi has been set up in your router or captive portal.
                    It will advance the onboarding status to{" "}
                    <strong>WiFi configured</strong>. You can still change the integration
                    type afterwards.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleMarkConfigured} disabled={busy}>
                    {marking ? "Saving…" : "Confirm"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </form>
    </div>
  )
}

function GeneralForm({
  property,
  allRegions,
  updateUrl,
}: {
  property: PropertyDetail
  allRegions: RegionOption[]
  updateUrl: string
}) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, patch, processing, isDirty } = useForm({
    name:              property.name,
    slug:              property.slug,
    property_type:     property.property_type,
    status:            property.status,
    region_id:         String(property.region_id),
    address:           property.address ?? "",
    contact_email:     property.contact_email ?? "",
    contact_phone:     property.contact_phone ?? "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(updateUrl)
  }

  return (
    <form onSubmit={handleSubmit} className="grid max-w-lg gap-5">
      <div className="grid gap-1.5">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={data.name}
          onChange={(e) => setData("name", e.target.value)}
          required
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          value={data.slug}
          onChange={(e) => setData("slug", e.target.value)}
          required
        />
        {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="property_type">Type</Label>
        <Select value={data.property_type} onValueChange={(v) => setData("property_type", v as PropertyType)}>
          <SelectTrigger id="property_type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(["hotel", "bnb", "apartment", "hostel", "villa", "other"] as PropertyType[]).map((t) => (
              <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="status">Status</Label>
        <Select value={data.status} onValueChange={(v) => setData("status", v as PropertyStatus)}>
          <SelectTrigger id="status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5">
        <Label>Onboarding status</Label>
        <div>
          <Badge variant="outline" className={onboardingClassName(property.onboarding_status)}>
            {onboardingLabel(property.onboarding_status)}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Set automatically as onboarding progresses. Use “Mark live” to publish — it
          enforces region readiness.
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="region_id">Region</Label>
        <Select value={data.region_id} onValueChange={(v) => setData("region_id", v)}>
          <SelectTrigger id="region_id"><SelectValue /></SelectTrigger>
          <SelectContent>
            {allRegions.map((r) => (
              <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="address">Address</Label>
        <Input id="address" value={data.address} onChange={(e) => setData("address", e.target.value)} />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="contact_email">Contact email</Label>
        <Input id="contact_email" type="email" value={data.contact_email} onChange={(e) => setData("contact_email", e.target.value)} />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="contact_phone">Contact phone</Label>
        <Input id="contact_phone" value={data.contact_phone} onChange={(e) => setData("contact_phone", e.target.value)} />
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={processing || !isDirty}>
          {processing ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </form>
  )
}

export default function PropertiesShow({ property, region_readiness, all_regions, can_suspend, can_reactivate, can_mark_live, urls }: Props) {
  const { props } = usePage<SharedProps>()
  const isAdmin = props.user_context?.is_admin
  const progress = onboardingProgress(property.onboarding_status)

  return (
    <div>
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
          <Link href={urls.index} className="hover:underline">Properties</Link>
          <span>/</span>
          <span>{property.name}</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{property.name}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant(property.status)} className="capitalize">
            {property.status}
          </Badge>
          <Badge variant="outline" className={onboardingClassName(property.onboarding_status)}>
            {onboardingLabel(property.onboarding_status)}
          </Badge>
          <span className="capitalize text-sm text-muted-foreground">{property.property_type}</span>
          {property.region_name && (
            <span className="text-sm text-muted-foreground">· {property.region_name}</span>
          )}
        </div>
      </div>

      {can_mark_live && property.onboarding_status !== "live" && (
        <div className="mb-6 max-w-2xl">
          {region_readiness && !region_readiness.ready ? (
            <RegionReadinessAlert
              readiness={region_readiness}
              markLiveUrl={urls.mark_live}
              canForce={Boolean(isAdmin)}
            />
          ) : (
            <GoLivePanel markLiveUrl={urls.mark_live} />
          )}
        </div>
      )}

      <div className="grid max-w-2xl gap-8">
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Owner
          </h2>
          <div className="grid gap-1 rounded-lg border p-4">
            <p className="text-sm font-medium">{property.owner_name || property.owner_email}</p>
            <p className="text-xs text-muted-foreground">{property.owner_email}</p>
          </div>
        </section>

        <Separator />

        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Branding
          </h2>
          <div className="flex items-center gap-4 rounded-lg border p-4">
            <div
              className="size-10 rounded-full border"
              style={{ backgroundColor: property.branding?.primary_color ?? "#e5e7eb" }}
              title={property.branding?.primary_color ?? "No color"}
            />
            <div>
              <p className="text-sm font-medium">
                {property.branding?.primary_color ?? (
                  <span className="italic text-muted-foreground">No brand color</span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {property.branding?.logo_url ? "Logo configured" : "No logo"}
              </p>
            </div>
          </div>
        </section>

        <Separator />

        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            WiFi setup
          </h2>
          <WifiSetupForm property={property} updateWifiUrl={urls.update_wifi} />
        </section>

        <Separator />

        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Content
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border p-4 text-center">
              <p className="text-2xl font-semibold">{property.pois_count}</p>
              <p className="mt-1 text-xs text-muted-foreground">POIs</p>
            </div>
            <div className="rounded-lg border p-4 text-center">
              <p className="text-2xl font-semibold">{property.guides_count}</p>
              <p className="mt-1 text-xs text-muted-foreground">Guides</p>
            </div>
            <div className="rounded-lg border p-4 text-center">
              <p className="text-2xl font-semibold">{property.guest_sessions_count}</p>
              <p className="mt-1 text-xs text-muted-foreground">Guest sessions</p>
            </div>
          </div>
        </section>

        <Separator />

        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Onboarding progress
          </h2>
          <div className="grid gap-3">
            <div className="flex items-center justify-between text-sm">
              <Badge variant="outline" className={onboardingClassName(property.onboarding_status)}>
                {onboardingLabel(property.onboarding_status)}
              </Badge>
              <span className="text-muted-foreground">{progress}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Pending</span>
              <span>Materials sent</span>
              <span>WiFi configured</span>
              <span>Live</span>
            </div>
            {property.onboarding_completed_at && (
              <p className="text-xs text-muted-foreground">
                Completed {new Date(property.onboarding_completed_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </section>

        {(can_suspend || can_reactivate) && (
          <>
            <Separator />
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Status
              </h2>
              <div className="flex flex-wrap gap-3">
                {can_suspend && property.status !== "suspended" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">Suspend property</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Suspend {property.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          The property will be suspended. Guests who scan QR codes or use WiFi redirect links will see a "temporarily unavailable" page until the property is reactivated.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => router.post(urls.suspend)}
                        >
                          Suspend property
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {can_reactivate && property.status === "suspended" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm">Reactivate property</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reactivate {property.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          The property will be reactivated. Existing QR codes and WiFi redirect links will work immediately.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => router.post(urls.reactivate)}>
                          Reactivate property
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </section>
          </>
        )}

        {isAdmin && (
          <>
            <Separator />
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Edit
              </h2>
              <GeneralForm property={property} allRegions={all_regions} updateUrl={urls.update} />
            </section>
          </>
        )}
      </div>
    </div>
  )
}

PropertiesShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
